/* Nether: bridge to Horizon's own section rendering.

   The bundle builder adds MULTIPLE lines in one request, which Horizon's
   product-form-component cannot express (it submits a single variant), so we
   post to /cart/add.js ourselves. The part we must NOT hand-roll is the
   refresh: Horizon re-renders the cart via morphSection, falling back to
   sectionRenderer when the response carries no section HTML
   (see component-cart-items.js #handleNoteUpdate for that exact pattern).

   Those live in ES modules reachable only through the theme's importmap, and
   the bundle picker's script is a classic (non-module) block script. This tiny
   module imports them once and exposes a single function.

   Bug it fixes: mixedAdd requested sections from `cart-items-component`, then
   relied on the drawer morphing itself from the event payload. On a page loaded
   with an EMPTY cart that morph never landed, so the customer added three pairs
   and the drawer slid up saying "your bag is empty" — items were in the cart,
   the drawer just never re-rendered. */
import { morphSection } from '@theme/section-renderer';

// Shopify's standard cart event. Hardcoded rather than imported from
// '@shopify/events' (a CDN module): if that import ever fails to resolve, the
// whole module dies and takes the safety net with it. Verified against a live
// add — type 'shopify:cart:lines-update', bubbles, carries `action`.
const CART_LINES_UPDATE = 'shopify:cart:lines-update';

/**
 * Re-render every cart section on the page.
 * @param {Record<string, string>} [sectionsHtml] - Section HTML from the add response, if any.
 */
async function refreshCartSections(sectionsHtml) {
  const components = document.querySelectorAll('cart-items-component[data-section-id]');
  const jobs = [];

  components.forEach((component) => {
    if (!(component instanceof HTMLElement)) return;
    const sectionId = component.dataset.sectionId;
    if (!sectionId) return;

    const isDrawer = Boolean(component.closest('cart-drawer-component, theme-drawer'));
    const mode = isDrawer ? 'hydration' : 'full';
    const provided = sectionsHtml && sectionsHtml[sectionId];

    // Always morph, and always inject the section stylesheet.
    //
    // When the cart was empty at page load, the drawer rendered the empty state
    // — which never emits the summary markup OR its `style[data-section-stylesheet]`.
    // Morphing the filled cart in without that stylesheet gave correct markup
    // with no styling at all: stock blue progress bar, unstyled discount link,
    // broken free-shipping line. `sectionRenderer.renderSection` cannot fix it
    // because only `morphSection` supports injectStylesheet, so we fetch the
    // section ourselves rather than fall back to it.
    //
    // Injection is idempotent (it replaces the existing stylesheet's contents),
    // so doing it unconditionally is cheaper than trying to detect "was empty" —
    // an earlier attempt at that detection failed because our custom empty state
    // renders .cart-drawer--empty, not Horizon's [data-cart-drawer-empty].
    const htmlPromise = provided
      ? Promise.resolve(provided)
      : fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        }).then((r) => r.text());

    jobs.push(htmlPromise.then((html) => morphSection(sectionId, html, { mode, injectStylesheet: true })));
  });

  const results = await Promise.allSettled(jobs);
  // A failed refresh must not look like a successful add.
  return results.every((r) => r.status === 'fulfilled');
}

window.NetherCart = Object.assign(window.NetherCart || {}, { refreshCartSections });

/* Safety net for EVERY add path, including stock Horizon's.

   Reproduced on a page loaded with an empty cart: add a pair through the plain
   add-to-cart button and the item lands server-side, but the drawer keeps its
   server-rendered empty markup. Horizon's own auto-open then reads
   `.cart-drawer--empty`, concludes the cart is empty, and never opens. So the
   customer gets no feedback at all, or an "empty" sheet.

   Rather than patch each caller, watch the standard event: if the drawer is
   still showing the empty state after an add settles while the cart actually
   has items, re-render and open. Costs one extra request only in the broken
   case. */
const cartIsStale = () => Boolean(document.querySelector('.cart-drawer--empty'));

async function openCartDrawerIfNeeded() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  const dialog = drawer.querySelector('dialog');
  if (dialog?.hasAttribute('open')) return;
  const host = drawer.closest('cart-drawer-component') || document.querySelector('cart-drawer-component');
  if (host && !host.hasAttribute('auto-open')) return;
  if (window.customElements?.whenDefined) await customElements.whenDefined('theme-drawer');
  if (typeof drawer.open === 'function') drawer.open();
}

let repairPending = false;

async function repairIfStale() {
  if (!cartIsStale()) return true; // normal morph landed — nothing to do
  const cartUrl = (window.Theme?.routes?.cart_url || '/cart') + '.js';
  const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then((r) => r.json())
    .catch(() => null);
  if (!cart?.item_count) return true; // genuinely empty
  await refreshCartSections();
  await openCartDrawerIfNeeded();
  return true;
}

document.addEventListener(CART_LINES_UPDATE, (event) => {
  if (event.action !== 'add' || repairPending) return;
  repairPending = true;

  // Deliberately NOT awaiting event.promise. On the stock product-form path it
  // was observed staying pending indefinitely (never resolving or rejecting),
  // which stalled this check forever. Instead: give the normal morph a beat to
  // land, then repair only if the drawer is still showing empty markup. The
  // second pass covers slow connections.
  const attempt = (delay, last) =>
    setTimeout(() => {
      repairIfStale()
        .catch((error) => console.warn('[nether-cart-refresh]', error))
        .finally(() => {
          if (last) repairPending = false;
        });
    }, delay);

  attempt(400, false);
  attempt(1600, true);
});

document.dispatchEvent(new CustomEvent('nether:cart-refresh-ready'));
